import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Root, Portal, Overlay, Close, Content, DialogTitle } from '@radix-ui/react-dialog';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { useGetAuthApi, useDeleteAuthApi } from '@/hooks/UseApiCalls';
import { ManagementSupportedDtoIn, RemoveSupportedFromFundResult } from '@/models/in/ManagementSupportedDtoIn';
import Fund from '@/models/Fund';
import deleteIcon from '@/assets/icons/delete.svg';
import closeIcon from '@/assets/icons/close.svg';
import styles from './RemoveFromFundDialog.module.css';

interface RemoveFromFundDialogProps {
  open: boolean;
  supported: ManagementSupportedDtoIn;
  onClose: () => void;
  /** Called after each successful removal, with the server's answer about the supported itself. */
  onSuccess: (result?: RemoveSupportedFromFundResult) => void;
}

const RemoveFromFundDialog = ({ open, supported, onClose, onSuccess }: RemoveFromFundDialogProps) => {
  const { t } = useTranslation();
  const [remainingFundIds, setRemainingFundIds] = useState<number[]>(supported.fundIds);
  const [fundToRemove, setFundToRemove] = useState<Fund | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: allFunds } = useGetAuthApi<Fund[]>(open ? 'funds' : '');
  const linkedFunds = (allFunds ?? []).filter((fund) => remainingFundIds.includes(fund.id));

  const { data: linkedRequestsData } = useGetAuthApi<{ count: number }>(
    fundToRemove ? `management/supporteds/${supported.id}/funds/${fundToRemove.id}/linked-support-requests-count` : ''
  );

  const { mutate: removeFromFund, isPending: isRemoving } = useDeleteAuthApi<RemoveSupportedFromFundResult>(
    fundToRemove ? `management/supporteds/${supported.id}/funds/${fundToRemove.id}` : ''
  );

  const supportedName = `${supported.firstName} ${supported.lastName}`;

  // Removing the supported's only fund leaves them unreachable, so the server deletes (archives) the
  // supported itself. The confirmation says so instead of blocking the removal.
  const isLastFund = remainingFundIds.length === 1;
  const linkedRequestsCount = linkedRequestsData?.count ?? 0;

  const confirmMessageKey = isLastFund
    ? (linkedRequestsCount > 0
      ? 'confirmDeleteSupportedMessageWithLinkedRequests'
      : 'confirmDeleteSupportedMessage')
    : (linkedRequestsCount > 0
      ? 'confirmMessageWithLinkedRequests'
      : 'confirmMessage');

  const confirmMessage = t(`supportedsManagement.removeFromFundDialog.${confirmMessageKey}`, {
    name: supportedName,
    fundName: fundToRemove?.name,
    count: linkedRequestsCount,
  });

  const handleDeleteClick = (fund: Fund) => {
    setError(null);
    setFundToRemove(fund);
  };

  const handleCancelDelete = () => {
    setFundToRemove(null);
  };

  const handleClose = () => {
    setError(null);
    setFundToRemove(null);
    onClose();
  };

  const handleConfirmDelete = () => {
    if (!fundToRemove) return;
    const removedFundId = fundToRemove.id;
    const wasLastFund = isLastFund;

    removeFromFund(undefined, {
      onSuccess: (result) => {
        setRemainingFundIds((prev) => prev.filter((id) => id !== removedFundId));
        setFundToRemove(null);
        onSuccess(result);
        // The supported itself is gone once its last fund was removed, so nothing is left to manage
        if (result?.supportedArchived ?? wasLastFund) {
          handleClose();
        }
      },
      onError: () => {
        setError(t('supportedsManagement.removeFromFundDialog.errorRemoving'));
        setFundToRemove(null);
      },
    });
  };

  return (
    <Root open={open} onOpenChange={(isOpen: boolean) => { if (!isOpen) handleClose(); }}>
      <Portal>
        <Overlay className={styles.overlay} />
        <Content className={styles.content} aria-describedby={undefined}>
          <Close className={styles.closeButton}>
            <img src={closeIcon} alt="" />
          </Close>
          <DialogTitle className={`heading-h3-semibold ${styles.title}`}>
            {t('supportedsManagement.removeFromFundDialog.title')}
          </DialogTitle>

          <div className="flex flex-col gap-3 p-6 min-w-[380px] max-w-[440px]">
            <p className="body-sm-regular text-(--font-body-1)">{t('supportedsManagement.removeFromFundDialog.description')}</p>

            {isLastFund && (
              <p className={`body-sm-regular ${styles.warning}`}>
                {t('supportedsManagement.removeFromFundDialog.lastFundDeletesSupported')}
              </p>
            )}

            {linkedFunds.length === 0 ? (
              <p className="body-sm-regular text-(--font-body-4)">{t('supportedsManagement.removeFromFundDialog.noFunds')}</p>
            ) : (
              <div className="flex flex-col">
                {linkedFunds.map((fund) => {
                  const buttonId = `remove-fund-${supported.id}-${fund.id}`;

                  return (
                    <div
                      key={fund.id}
                      className="group flex items-center justify-between gap-3 py-2 px-1 rounded hover:bg-(--surface-secondary-lowest)"
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="body-lr-semibold text-(--font-body-1)">{fund.id}</span>
                        <span className="body-lr-regular text-(--font-body-1) truncate">{fund.name}</span>
                      </span>
                      <button
                        type="button"
                        id={buttonId}
                        title={t('supportedsManagement.removeFromFund')}
                        aria-label={t('supportedsManagement.removeFromFund')}
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDeleteClick(fund)}
                      >
                        <img src={deleteIcon} alt="" className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {error && <p className="text-red-500 body-sm-regular">{error}</p>}
          </div>
        </Content>
      </Portal>

      <ConfirmationDialog
        open={fundToRemove !== null}
        onOpenChange={(isOpen) => { if (!isOpen) handleCancelDelete(); }}
        title={isLastFund
          ? t('supportedsManagement.removeFromFundDialog.confirmDeleteSupportedTitle')
          : t('supportedsManagement.removeFromFundDialog.confirmTitle')}
        message={confirmMessage}
        confirmText={isLastFund
          ? 'supportedsManagement.removeFromFundDialog.confirmDeleteSupportedButton'
          : 'supportedsManagement.removeFromFundDialog.confirmButton'}
        cancelText="cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isRemoving}
        variant="danger"
      />
    </Root>
  );
};

export default RemoveFromFundDialog;
